import React, { useEffect, useState } from 'react';
import Update from '../Update';
import Media from '../Media';
import graph from './graph';
import ChangeStatus from '../ChangeStatus';
import ChangeLocation from '../ChangeLocation';
import HistoryCenter from '../HistoryCenter';
import { digitsFaToEn } from '@persian-tools/persian-tools';
import jMoment from 'moment-jalaali';

import {
  Card,
  Stack,
  Fade,
  Divider,
  Checkbox,
  Typography,
  CardHeader,
  CardActionArea,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  alpha,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { AvatarPopover, CardHeaderTitle, NewSpeedDial, SelectableChipLabel } from 'components';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { hasRequiredRole } from 'helpers';
import { TaskStatusModel } from 'models';
import { useMutation } from '@apollo/client';
import { isEmptyObject } from 'helpers/formatObject';
import { toast } from 'react-toastify';

export default function Model({ model, delay, direction, checked, handleSelect, selectOnClick, refetch, color }) {
  const { isAuthenticated, userInfo } = useSelector((state) => state.auth);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { defaultMatches: false });

  const newDate = digitsFaToEn(jMoment(model?.start_time).format('HH:mm:ss jYYYY/jMM/jDD'));

  const chips = [
    { id: '1', name: 'taskType', title: model?.type?.title },
    { id: '2', name: 'operationType', title: model?.operation_type?.title },
    { id: '3', name: 'materialType', title: model?.material_type?.title },
    { id: '4', name: 'vehicleType', title: model?.vehicle?.type?.title },
    { id: '5', name: 'plaque', title: model?.vehicle?.plaque },
    { id: '6', name: 'workshop', title: model?.workshop?.title },
    { id: '7', name: 'site', title: model?.site?.title },
  ];
  const driverChips = [
    { id: '1', name: 'taskType', title: model?.type?.title },
    { id: '2', name: 'operationType', title: model?.operation_type?.title },
    { id: '3', name: 'materialType', title: model?.material_type?.title },
    { id: '4', name: 'vehicleType', title: model?.vehicle?.type?.title },
    { id: '5', name: 'plaque', title: model?.vehicle?.plaque },
    { id: '6', name: 'workshop', title: model?.workshop?.title },
  ];

  const status = model?.status?.id;
  const [selected, setSelected] = useState([model?.status]);

  const onAssign = (data, onClose) => {
    // const { id, title } = data[0];
    changeStatus(data[0]);
    // setSelected([{ id, title }]);
    // onChange(String(id));
    onClose();
  };

  const { userToken } = useSelector((state) => state.auth);

  const [handleDelete, { loading }] = useMutation(graph.delete.query, {
    context: {
      serviceName: graph.delete.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const changeStatus = async (status) => {
    try {
      const { data, errors } = await handleDelete({
        variables: { ids: model.id, status_id: status.id },
      });
      if (!errors) {
        refetch();
        if (!isEmptyObject(data)) {
          data[graph.delete.name]?.messages.map((message) => toast.success(String(message)));
        }
      }
    } catch (err) {
      toast.error(err);
    }
  };

  const [updateOpen, setUpdateOpen] = useState(false);
  const canUpdate =
    isAuthenticated &&
    hasRequiredRole(['superadmin', 'workshopManager', 'workshopSupervisor', 'siteManager'], userInfo?.roles);
  const handleRowClick = () => (canUpdate && !selectOnClick ? setUpdateOpen(true) : handleSelect());

  return (
    <>
      <Fade in unmountOnExit timeout={{ appear: delay * 100, enter: delay * 120, exit: delay * 140 }}>
        <Card
          sx={{
            position: 'relative',
            // bgcolor: checked && 'action.disabledBackground',
            bgcolor: checked ? 'primary.light' : color ? 'action.disabledOpacity' : 'action.focus',
          }}
        >
          <Stack direction="row" alignItems="center" columnGap={0.5}>
            <Stack flexShrink={0} alignItems="center" justifyContent="center" sx={{ pl: 0.5 }}>
              <Checkbox size="small" checked={checked} onChange={handleSelect} />
            </Stack>
            <CardActionArea onClick={handleRowClick} sx={{ flex: 1, minWidth: 0 }}>
              <CardHeader
                sx={{ px: 0.5, py: 1 }}
                title={
                  <Stack
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    direction={!isMobile ? 'row' : 'column'}
                    columnGap={1}
                    rowGap={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Stack direction="column" flexShrink={0}>
                      <Typography fontSize={14} variant="subtitle1">
                        <FormattedMessage id="driver" />
                        {model?.driver?.firstname && ` : ${model?.driver?.firstname} `}
                        {model?.driver?.lastname && `${model?.driver?.lastname} `}
                      </Typography>
                      <Typography fontSize={14} variant="subtitle1">
                        <FormattedMessage id="supervisor" />
                        {model?.supervisor?.firstname && ` : ${model?.supervisor?.firstname} `}
                        {model?.supervisor?.lastname && `${model?.supervisor?.lastname} `}
                      </Typography>
                      <Typography fontSize={14} variant="subtitle1">
                        {`(${newDate})`}
                      </Typography>
                    </Stack>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      direction="row"
                      columnGap={1}
                      flexWrap="wrap"
                      rowGap={0.5}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {(isAuthenticated && hasRequiredRole(['driver'], userInfo?.roles) ? driverChips : chips).map(
                        (chip) => (
                          <Chip
                            size="small"
                            key={chip.id}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                            label={<SelectableChipLabel>{chip?.title || '----'}</SelectableChipLabel>}
                            sx={{
                              width: 160,
                              flexShrink: 0,
                              userSelect: 'text',
                              WebkitUserSelect: 'text',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                              },
                            }}
                          />
                        )
                      )}
                    </Stack>
                  </Stack>
                }
              />
            </CardActionArea>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              columnGap={0.5}
              rowGap={0.5}
              flexWrap="wrap"
              flexShrink={0}
              sx={{ pr: 1, pl: 0.5, py: 0.5, zIndex: 1 }}
            >
              <NewSpeedDial>
                <Update ids={model.id} title={<FormattedMessage id="update" />} refetch={refetch} />
                <HistoryCenter model={model} title={<FormattedMessage id="history" />} refetch={refetch} />
                <ChangeLocation
                  ids={model?.vehicle?.id}
                  title={<FormattedMessage id="set_location" />}
                  refetch={refetch}
                />
                <TaskStatusModel isAssign isPopup onAssign={onAssign} preSelected={selected}>
                  <Tooltip title={<FormattedMessage id="change_status" />}>
                    {isAuthenticated &&
                      hasRequiredRole(
                        [
                          'superadmin',
                          'operator',
                          'companyCeo',
                          'companyOperator',
                          'companyFinancial',
                          'workshopSupervisor',
                        ],
                        userInfo?.roles
                      ) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color={status === '2' ? 'success' : 'error'}
                          endIcon={status === '2' ? <CheckCircleIcon /> : <ErrorIcon />}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {model?.status?.title}
                        </Button>
                      )}
                  </Tooltip>
                </TaskStatusModel>
              </NewSpeedDial>
            </Stack>
          </Stack>
        </Card>
      </Fade>
      {updateOpen && (
        <Update
          open
          ids={model.id}
          refetch={refetch}
          title={<FormattedMessage id="update" />}
          onClose={() => setUpdateOpen(false)}
        />
      )}
      <Divider variant="middle" />
    </>
  );
}
